import { SDKPlatform, SDKStatusCode } from "@nebula-sdk/core";
import { SDKBrower } from "../core/enum";
import type { NebulaWEBGameSDKAccount } from "../account";
import type {
  WEBSDKPaymentRequestAlipayPayModel as WEBSDKPaymentRequestAlipayPaymentModel, WEBSDKPaymentRequestOrderQueryModel,
  WEBSDKPaymentRequestPayOrderModel, WEBSDKPaymentRequestProductInfoModel, WEBSDKPaymentRequestWechatH5PayModel as WEBSDKPaymentRequestWechatH5PaymentModel,
  WEBSDKPaymentRequestWechatJsapiPayModel as WEBSDKPaymentRequestWechatJsapiPaymentModel,
} from "./model";
import type {
  WEBDKPaymentCreatePayOrderArgs, WEBDKPaymentRequestAlipayPaymentArgs, WEBDKPaymentRequestOrderQueryArgs,
  WEBDKPaymentRequestPayOrderArgs, WEBDKPaymentRequestProductInfoArgs, WEBDKPaymentRequestWechatH5PaymentArgs,
  WEBDKPaymentRequestWechatJsapiPaymentArgs, WEBDKPaymentStartAlipayPaymentArgs, WEBDKPaymentStartWechatPaymentArgs,
  WEBSDKPaymentCreatePayOrderResult, WEBSDKPaymentRequestAlipayPaymentResult, WEBSDKPaymentRequestOrderQueryResult,
  WEBSDKPaymentRequestPayOrderResult, WEBSDKPaymentRequestProductInfoResult, WEBSDKPaymentRequestWechatH5PaymentResult,
  WEBSDKPaymentRequestWechatJsapiPaymentResult, WEBSDKPaymentStartAlipayPaymentResult, WEBSDKPaymentStartWechatPaymentResult,
} from "./type";
import type { NebulaWEBGameSDKCore } from "../core";

declare global {
  interface Window {
    WeixinJSBridge: {
      invoke: (
        action: string,
        options: Record<string, unknown>,
        callback: (result: { err_msg?: string }) => void,
      ) => void;
      call: (action: string) => void;
    };
  }
}

// 支付服务
export class NebulaWEBGameSDKPayment {
  // 构造函数
  public constructor(core: NebulaWEBGameSDKCore, account: NebulaWEBGameSDKAccount) {
    this.core = core;
    this.account = account;
  }

  // 核心服务
  private readonly core: NebulaWEBGameSDKCore;

  // 账户服务
  private readonly account: NebulaWEBGameSDKAccount;

  // 生成支付回调地址
  private createReturnUrl(orderId: string, verifyToken: string): string {
    const query = [
      `order_id=${orderId}`,
      `verify_token=${verifyToken}`,
      `union_id=${this.core.userInfo!.nbUnionId}`,
      `open_id=${this.core.userInfo!.nbOpenId}`,
    ];
    return `${window.location.origin}${window.location.pathname}?${query.join("&")}`;
  }

  // 打开支付重定向地址
  private openRedirectUrl(url: string): void {
    if (this.core.browser === SDKBrower.Unknown) {
      const reference = window.open();
      if (reference) {
        reference.location.href = url;
      } else {
        window.location.href = url;
      }
    } else {
      window.location.href = url;
    }
  }

  // 获取商品信息
  private async requestProductInfo(
    args: WEBDKPaymentRequestProductInfoArgs,
  ): Promise<WEBSDKPaymentRequestProductInfoResult> {
    const result = await this.core.scopeRequestPassport<
      Array<WEBSDKPaymentRequestProductInfoModel>
    >({
      url: "/pay/goods/list",
      method: "GET",
      data: { app_id: this.core.options?.config.nbAppId },
    });

    if (result.status === "error") {
      this.core.log({ code: SDKStatusCode.Error, msg: "获取商品信息失败", data: result });
      void this.core.scopeRequestTrack({
        eventId: "requestProductInfo",
        eventName: "获取商品信息失败",
        eventGroup: "payment",
        eventValue: { args, result },
        eventLevel: "error",
      });
      return { status: "error", code: SDKStatusCode.Error, msg: "获取商品信息失败", data: null };
    }

    const productInfo = result.data?.find((product) => {
      return product.goods_id === args.productId;
    });
    if (!productInfo) {
      void this.core.scopeRequestTrack({
        eventId: "requestProductInfo",
        eventName: "获取商品信息成功",
        eventGroup: "payment",
        eventValue: { args, result },
        eventLevel: "info",
      });
      return { status: "error", code: SDKStatusCode.Error, msg: "商品信息不存在", data: null };
    }

    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "获取商品信息成功",
      data: {
        productId: productInfo.goods_id,
        productName: productInfo.goods_name,
        productPrice: productInfo.goods_amount,
        productDesc: productInfo.goods_desc,
      },
    };
  }

  // 创建聚合支付订单
  private async requestPayOrder(
    args: WEBDKPaymentRequestPayOrderArgs,
  ): Promise<WEBSDKPaymentRequestPayOrderResult> {
    const result = await this.core.scopeRequestPassport<WEBSDKPaymentRequestPayOrderModel>({
      url: "/pay/order/webCreate",
      method: "POST",
      data: {
        app_id: this.core.options!.config.nbAppId,
        token: this.core.userInfo?.token,
        os: 2,
        open_id: this.core.userInfo?.nbOpenId,
        union_id: this.core.userInfo?.nbUnionId,
        pay_amount: args.data.goodsPrice,
        goods_id: args.data.goodsId,
        goods_num: 1,
        goods_name: args.data.goodsName,
        channel_id: this.core.options!.config.nbChannelId,
        player_id: args.data.playerId,
        player_name: args.data.playerName,
        player_level: args.data.playerLevel,
        player_channel_id: args.extra.playerChannelId,
        vip_level: args.data.vipLevel,
        server_id: args.data.serverId,
        param_ext: JSON.stringify({
          wxpay_app_id: this.account.authorize?.appId,
          wxpay_open_id: this.account.authorize?.openId,
        }),
        pay_source: args.extra.playerPaySource,
        ua: navigator.userAgent.slice(0, 250),
      },
    });
    if (result.status === "error") {
      this.core.log({
        code: SDKStatusCode.AggregateOrderError,
        msg: "创建聚合订单失败",
        data: result,
      });
      void this.core.scopeRequestTrack({
        eventId: "requestPayOrder",
        eventName: "创建聚合订单失败",
        eventGroup: "payment",
        eventValue: { args, result },
        eventLevel: "error",
      });

      if (result.code === 3110339) {
        return {
          status: "error",
          code: SDKStatusCode.PayAccountExpiredError,
          msg: result.msg,
          data: null,
        };
      }
      if (result.code === 3110340) {
        return {
          status: "error",
          code: SDKStatusCode.PayAntiAddictionError,
          msg: result.msg,
          data: null,
        };
      }
      return {
        status: "error",
        code: SDKStatusCode.AggregateOrderError,
        msg: result.msg,
        data: null,
      };
    }

    void this.core.scopeRequestTrack({
      eventId: "requestPayOrder",
      eventName: "创建聚合订单成功",
      eventGroup: "payment",
      eventValue: { args, result },
      eventLevel: "info",
    });
    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "创建聚合订单成功",
      data: { orderNo: result.data.order_id, orderToken: result.data.verify_token },
    };
  }

  // 查询订单支付状态
  public async requestOrderQuery(
    args: WEBDKPaymentRequestOrderQueryArgs,
  ): Promise<WEBSDKPaymentRequestOrderQueryResult> {
    const result = await this.core.scopeRequestPassport<WEBSDKPaymentRequestOrderQueryModel>({
      url: "/pay/order/webQuery",
      method: "GET",
      data: {
        order_id: args.orderNo,
        verify_token: args.orderToken,
        open_id: this.core.userInfo?.nbOpenId,
        union_id: this.core.userInfo?.nbUnionId,
      },
    });
    if (result.status === "error") {
      this.core.log({
        code: SDKStatusCode.Error,
        msg: "查询订单状态失败",
        data: result,
      });
      void this.core.scopeRequestTrack({
        eventId: "requestOrderQuery",
        eventName: "查询订单状态失败",
        eventGroup: "payment",
        eventValue: { args, result },
        eventLevel: "error",
      });
      return {
        status: "error",
        code: SDKStatusCode.Error,
        msg: "查询订单状态失败",
        data: null,
      };
    }

    void this.core.scopeRequestTrack({
      eventId: "requestOrderQuery",
      eventName: "查询订单状态成功",
      eventGroup: "payment",
      eventValue: { args, result },
      eventLevel: "info",
    });
    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "查询订单状态成功",
      data: { status: result.data.status },
    };
  }

  // 请求支付宝H5支付地址
  private async requestAlipayPayment(
    args: WEBDKPaymentRequestAlipayPaymentArgs,
  ): Promise<WEBSDKPaymentRequestAlipayPaymentResult> {
    const result = await this.core.scopeRequestPassport<WEBSDKPaymentRequestAlipayPaymentModel>({
      url: "/pay/alipay/webH5Pay",
      method: "POST",
      data: {
        app_id: this.core.options!.config.nbAppId,
        token: this.core.userInfo?.token,
        open_id: this.core.userInfo?.nbOpenId,
        union_id: this.core.userInfo?.nbUnionId,
        channel_id: this.core.options!.config.nbChannelId,
        order_id: args.orderNo,
        platform_method: "wap",
        verify_token: args.orderToken,
        return_url: this.createReturnUrl(args.orderNo, args.orderToken),
      },
    });

    if (result.status === "error") {
      this.core.log({
        code: SDKStatusCode.Error,
        msg: "获取支付宝支付地址失败",
        data: result,
      });
      void this.core.scopeRequestTrack({
        eventId: "requestAlipayPayment",
        eventName: "获取支付宝支付地址失败",
        eventGroup: "payment",
        eventValue: { args, result },
        eventLevel: "error",
      });
      if (result.code === 3110339) {
        return {
          status: "error",
          code: SDKStatusCode.PayAccountExpiredError,
          msg: result.msg,
          data: null,
        };
      }
      return {
        status: "error",
        code: SDKStatusCode.Error,
        msg: "获取支付宝支付地址失败",
        data: null,
      };
    }

    void this.core.scopeRequestTrack({
      eventId: "requestAlipayPayment",
      eventName: "获取支付宝支付地址成功",
      eventGroup: "payment",
      eventValue: { args, result },
      eventLevel: "info",
    });
    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "获取支付宝支付地址成功",
      data: { redirectUrl: result.data.redirect_url },
    };
  }

  // 请求微信H5支付地址
  private async requestWechatH5Payment(
    args: WEBDKPaymentRequestWechatH5PaymentArgs,
  ): Promise<WEBSDKPaymentRequestWechatH5PaymentResult> {
    const result = await this.core.scopeRequestPassport<WEBSDKPaymentRequestWechatH5PaymentModel>({
      url: "/pay/wxpay/webH5Pay",
      method: "POST",
      data: {
        app_id: this.core.options!.config.nbAppId,
        token: this.core.userInfo?.token,
        open_id: this.core.userInfo?.nbOpenId,
        union_id: this.core.userInfo?.nbUnionId,
        channel_id: this.core.options!.config.nbChannelId,
        order_id: args.orderNo,
        platform_method: "wap",
        verify_token: args.orderToken,
        return_url: this.createReturnUrl(args.orderNo, args.orderToken),
      },
    });
    if (result.status === "error") {
      this.core.log({
        code: SDKStatusCode.Error,
        msg: "获取微信支付地址失败",
        data: result,
      });
      void this.core.scopeRequestTrack({
        eventId: "requestWechatH5Payment",
        eventName: "获取微信支付地址失败",
        eventGroup: "payment",
        eventValue: { args, result },
        eventLevel: "error",
      });
      if (result.code === 3110339) {
        return {
          status: "error",
          code: SDKStatusCode.PayAccountExpiredError,
          msg: result.msg,
          data: null,
        };
      }
      return {
        status: "error",
        code: SDKStatusCode.Error,
        msg: "获取微信支付地址失败",
        data: null,
      };
    }

    void this.core.scopeRequestTrack({
      eventId: "requestWechatH5Payment",
      eventName: "获取微信支付地址成功",
      eventGroup: "payment",
      eventValue: { args, result },
      eventLevel: "info",
    });
    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "获取微信支付地址成功",
      data: { redirectUrl: result.data.redirect_url },
    };
  }

  // 请求微信JSAPI支付参数
  private async requestWechatJsapiPayment(
    args: WEBDKPaymentRequestWechatJsapiPaymentArgs,
  ): Promise<WEBSDKPaymentRequestWechatJsapiPaymentResult> {
    const result =
      await this.core.scopeRequestPassport<WEBSDKPaymentRequestWechatJsapiPaymentModel>({
        url: "/pay/wxpay/webJsapiPay",
        method: "POST",
        data: {
          app_id: this.core.options!.config.nbAppId,
          token: this.core.userInfo?.token,
          open_id: this.core.userInfo?.nbOpenId,
          union_id: this.core.userInfo?.nbUnionId,
          channel_id: this.core.options!.config.nbChannelId,
          order_id: args.orderNo,
          platform_method: "webJsapi",
          verify_token: args.orderToken,
        },
      });
    if (result.status === "error") {
      this.core.log({
        code: SDKStatusCode.Error,
        msg: "获取微信JSAPI支付参数失败",
        data: result,
      });
      void this.core.scopeRequestTrack({
        eventId: "requestWechatJsapiPayment",
        eventName: "获取微信JSAPI支付参数失败",
        eventGroup: "payment",
        eventValue: { args, result },
        eventLevel: "error",
      });
      if (result.code === 3110339) {
        return {
          status: "error",
          code: SDKStatusCode.PayAccountExpiredError,
          msg: result.msg,
          data: null,
        };
      }
      return {
        status: "error",
        code: SDKStatusCode.Error,
        msg: "获取微信JSAPI支付参数失败",
        data: null,
      };
    }

    void this.core.scopeRequestTrack({
      eventId: "requestWechatJsapiPayment",
      eventName: "获取微信JSAPI支付参数成功",
      eventGroup: "payment",
      eventValue: { args, result },
      eventLevel: "info",
    });
    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "获取微信JSAPI支付参数成功",
      data: { jsapiParams: result.data.jsapi_params },
    };
  }

  // 创建支付订单
  public async createPayOrder(
    args: WEBDKPaymentCreatePayOrderArgs,
  ): Promise<WEBSDKPaymentCreatePayOrderResult> {
    const initResult = await this.core.validate(["init"]);
    if (initResult.status === "error") return initResult;

    const loginResult = await this.core.validate(["login"]);
    if (loginResult.status === "error") return loginResult;

    const extra = { playerChannelId: "", playerPaySource: 99 };

    const [playerResult, productResult] = await Promise.all([
      this.account.requestPlayerInfo({
        playerId: args.playerId,
        serverId: args.serverId,
      }),
      this.requestProductInfo({ productId: args.goodsId }),
    ]);
    if (playerResult.status === "success") {
      extra.playerChannelId = playerResult.data!.channelId;
    }

    // 1-微信生态、2-支付宝生态、3-Apple手机、4-iPAD、5-Android手机、6-PC、99-其他
    if (this.core.browser === SDKBrower.WeChat) {
      extra.playerPaySource = 1;
    } else if (this.core.browser === SDKBrower.Alipay) {
      extra.playerPaySource = 2;
    } else if (this.core.platform === SDKPlatform.IOS) {
      extra.playerPaySource = /ipad/i.test(navigator.userAgent) ? 4 : 3;
    } else if (this.core.platform === SDKPlatform.Android) {
      extra.playerPaySource = 5;
    } else if (
      this.core.platform === SDKPlatform.Windows ||
      this.core.platform === SDKPlatform.Mac
    ) {
      extra.playerPaySource = 6;
    } else {
      extra.playerPaySource = 99;
    }

    const orderResult = await this.requestPayOrder({ data: args, extra });
    if (orderResult.status === "error") {
      if (orderResult.code === SDKStatusCode.PayAccountExpiredError) {
        this.core.userInfo = null;
      }
      return orderResult as WEBSDKPaymentCreatePayOrderResult;
    }

    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "创建支付订单成功",
      data: {
        orderNo: orderResult.data!.orderNo,
        orderToken: orderResult.data!.orderToken,
        playerId: args.playerId,
        playerName: args.playerName,
        serverId: playerResult.data?.serverId ?? "",
        serverName: playerResult.data?.serverName ?? "",
        productId: args.goodsId,
        productName: args.goodsName,
        productPrice: args.goodsPrice,
        productDesc: productResult.data?.productDesc ?? "",
      },
    };
  }

  public async startAplipayPayment(
    args: WEBDKPaymentStartAlipayPaymentArgs,
  ): Promise<WEBSDKPaymentStartAlipayPaymentResult> {
    const initResult = await this.core.validate(["init"]);
    if (initResult.status === "error") return initResult;

    const loginResult = await this.core.validate(["login"]);
    if (loginResult.status === "error") return loginResult;

    const payResult = await this.requestAlipayPayment(args);
    if (payResult.status === "error") {
      if (payResult.code === SDKStatusCode.PayAccountExpiredError) {
        this.core.userInfo = null;
      }
      return payResult;
    }

    if (this.core.browser === SDKBrower.WeChat) {
      return {
        status: "error",
        code: SDKStatusCode.PayNoSupportBrowserError,
        msg: "微信浏览器不支持支付宝支付，请使用其他浏览器",
        data: { redirectUrl: payResult.data!.redirectUrl },
      };
    }

    this.openRedirectUrl(payResult.data!.redirectUrl);

    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "打开支付宝支付页面成功",
      data: { redirectUrl: payResult.data!.redirectUrl },
    };
  }

  public async startWechatPayment(
    args: WEBDKPaymentStartWechatPaymentArgs,
  ): Promise<WEBSDKPaymentStartWechatPaymentResult> {
    const initResult = await this.core.validate(["init"]);
    if (initResult.status === "error") return initResult;

    const loginResult = await this.core.validate(["login"]);
    if (loginResult.status === "error") return loginResult;

    if (this.core.browser !== SDKBrower.WeChat) {
      const payResult = await this.requestWechatH5Payment(args);
      if (payResult.status === "error") {
        if (payResult.code === SDKStatusCode.PayAccountExpiredError) {
          this.core.userInfo = null;
        }
        return payResult;
      }

      this.openRedirectUrl(payResult.data!.redirectUrl);

      return {
        status: "success",
        code: SDKStatusCode.Success,
        msg: "打开微信支付页面成功",
        data: { redirectUrl: payResult.data!.redirectUrl },
      };
    }

    const payResult = await this.requestWechatJsapiPayment(args);
    if (payResult.status === "error") {
      if (payResult.code === SDKStatusCode.PayAccountExpiredError) {
        this.core.userInfo = null;
      }
      return payResult as WEBSDKPaymentStartWechatPaymentResult;
    }
    if (!window.WeixinJSBridge) {
      return {
        status: "error",
        code: SDKStatusCode.PayNoSupportBrowserError,
        msg: "当前环境不支持微信支付",
        data: null,
      };
    }
    return await new Promise((resolve) => {
      window.WeixinJSBridge.invoke("getBrandWCPayRequest", payResult.data!.jsapiParams, (
        result,
      ) => {
        if (result.err_msg === "get_brand_wcpay_request:cancel") {
          void this.core.scopeRequestTrack({
            eventId: "startWechatPayment",
            eventName: "拉起微信支付取消",
            eventGroup: "payment",
            eventValue: { args, result },
            eventLevel: "info",
          });
          resolve({
            status: "error",
            code: SDKStatusCode.PayGoodsDirectCancelError,
            msg: "拉起微信支付取消",
            data: null,
          });
          return;
        }

        if (result.err_msg === "get_brand_wcpay_request:fail") {
          this.core.log({
            code: SDKStatusCode.PayGoodsDirectError,
            msg: "拉起微信支付失败",
            data: result,
          });
          void this.core.scopeRequestTrack({
            eventId: "startWechatPayment",
            eventName: "拉起微信支付失败",
            eventGroup: "payment",
            eventValue: { args, result },
            eventLevel: "error",
          });
          resolve({
            status: "error",
            code: SDKStatusCode.PayGoodsDirectError,
            msg: "拉起微信支付失败",
            data: null,
          });
          return;
        }

        void this.core.scopeRequestTrack({
          eventId: "startWechatPayment",
          eventName: "拉起微信支付成功",
          eventGroup: "payment",
          eventValue: { args, result },
          eventLevel: "info",
        });
        resolve({
          status: "success",
          code: SDKStatusCode.Success,
          msg: "拉起微信支付成功",
          data: null,
        });
      });
    });
  }
}
